import { useRef, useState } from 'react';
import {
    motion,
    useScroll,
    useSpring,
    useMotionValue,
    useMotionValueEvent,
    useTransform,
    useMotionTemplate,
} from 'framer-motion';

/**
 * HowIThink — serpentine "process" section (inspired by the six2eight
 * "Our Process" layout). Giant ghost numbers to the left of each step,
 * steps alternating left/right, a dashed curved path that weaves through
 * the empty corridor (kept off the text), a lime trail that draws in, and
 * Himanshu's own avatar riding along the path as you scroll.
 */

const steps = [
    {
        n: '1',
        stage: 'Discover',
        title: 'Research',
        body: 'We begin with a thorough investigation into your goals, users and product to frame what really needs fixing.',
        pos: { left: '2%', top: '10%' },
    },
    {
        n: '2',
        stage: 'Define',
        title: 'Strategy',
        body: 'We study behaviour, map out journeys and define a plan that balances real usability with business objectives.',
        pos: { left: '58%', top: '30%' },
    },
    {
        n: '3',
        stage: 'Design',
        title: 'Craft',
        body: 'We sketch clean, goal-driven flows, then turn them into polished, responsive designs that feel simple to use.',
        pos: { left: '2%', top: '50%' },
    },
    {
        n: '4',
        stage: 'Systemise',
        title: 'Scale',
        body: 'We build reusable design systems so the next feature costs a fraction of the first and stays consistent.',
        pos: { left: '58%', top: '70%' },
    },
    {
        n: '5',
        stage: 'Ship & Learn',
        title: 'Deliver',
        body: 'We hand off dev-ready files, help ship, and stay close after launch, treating every release as something to learn from.',
        pos: { left: '2%', top: '90%' },
    },
];

// Path weaves through the central corridor, touching near each step's
// number / text edge rather than crossing the text body.
const VB_W = 1000;
const VB_H = 720;
const PATH_D =
    'M360,92 C360,190 620,150 620,216 C620,300 360,270 360,360 C360,450 620,420 620,504 C620,590 360,560 360,628';

// Scroll-progress fractions at which each step lights up + reveals its copy.
const STEP_THRESHOLDS = [0, 0.2, 0.45, 0.7, 0.9];

const StepBlock = ({ n, stage, title, body, pos, active }) => (
    <div
        className="absolute w-[40%] flex items-center gap-4 lg:gap-6"
        style={{ left: pos.left, top: pos.top, transform: 'translateY(-50%)' }}
    >
        <span
            aria-hidden
            className={`pointer-events-none shrink-0 font-display leading-none text-[100px] lg:text-[128px] select-none tabular-nums transition-[color,opacity,filter] duration-700 ease-out ${
                active
                    ? 'text-[var(--accent)] opacity-90 drop-shadow-[0_0_22px_rgba(216,254,91,0.45)]'
                    : 'text-[var(--ink)] opacity-[0.09]'
            }`}
        >
            {n}
        </span>
        <motion.div
            animate={{ opacity: active ? 1 : 0.16, y: active ? 0 : 18 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
            <div className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-[var(--accent)]">
                {stage}
            </div>
            <h3 className="mt-2 font-display leading-[1.02] text-3xl lg:text-[40px] text-[var(--ink)]">
                {title}
                <span className="text-[var(--accent)]">.</span>
            </h3>
            <p className="mt-3 max-w-[30ch] font-sans-brand text-[13px] lg:text-[13.5px] leading-[1.6] text-[var(--ink-2)]">
                {body}
            </p>
        </motion.div>
    </div>
);

export const HowIThink = () => {
    const sectionRef = useRef(null);
    const pathRef = useRef(null);
    const [reached, setReached] = useState(1);

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start 0.85', 'end 0.45'],
    });
    const smooth = useSpring(scrollYProgress, {
        stiffness: 90,
        damping: 24,
        mass: 0.4,
    });

    // Point on the path (viewBox coords) driven by scroll progress.
    const vx = useMotionValue(360);
    const vy = useMotionValue(92);
    useMotionValueEvent(smooth, 'change', (v) => {
        const el = pathRef.current;
        if (el) {
            const L = el.getTotalLength();
            if (L > 0) {
                const p = el.getPointAtLength(Math.min(Math.max(v, 0), 1) * L);
                vx.set(p.x);
                vy.set(p.y);
            }
        }
        let r = 0;
        for (const t of STEP_THRESHOLDS) if (v >= t) r++;
        setReached((prev) => (prev === r ? prev : r));
    });
    // Convert viewBox coords → container percentages (SVG uses meet with a
    // matching aspect box, so this maps 1:1 with the drawn path).
    const left = useMotionTemplate`${useTransform(vx, (x) => (x / VB_W) * 100)}%`;
    const top = useMotionTemplate`${useTransform(vy, (y) => (y / VB_H) * 100)}%`;

    return (
        <section
            id="how-i-think"
            data-testid="how-i-think"
            className="relative py-10 md:py-14 border-t border-[var(--line)]"
        >
            <div className="mx-auto max-w-[1400px] px-4 md:px-6">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-12">
                    <div className="max-w-2xl">
                        <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-[var(--accent)]">
                            My process
                        </div>
                        <h2 className="mt-3 font-display leading-[0.95] text-4xl md:text-6xl text-[var(--ink)]">
                            How I{' '}
                            <span className="font-display-italic">think</span>
                            <span className="text-[var(--accent)]">.</span>
                        </h2>
                    </div>
                    <p className="max-w-md font-sans-brand text-[14px] leading-[1.6] text-[var(--ink-2)]">
                        Five repeatable moves, biased toward learning early
                        instead of shipping late. Follow me down the line.
                    </p>
                </div>

                {/* Desktop: serpentine path + avatar marker */}
                <div
                    ref={sectionRef}
                    className="hidden md:block relative w-full max-w-[1320px] mx-auto"
                    style={{ aspectRatio: `${VB_W} / ${VB_H}` }}
                >
                    <svg
                        viewBox={`0 0 ${VB_W} ${VB_H}`}
                        className="absolute inset-0 w-full h-full"
                        fill="none"
                        aria-hidden
                    >
                        <path
                            ref={pathRef}
                            d={PATH_D}
                            stroke="var(--line-2)"
                            strokeWidth="2"
                            strokeDasharray="3 12"
                            strokeLinecap="round"
                        />
                        <motion.path
                            d={PATH_D}
                            stroke="var(--accent)"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            style={{ pathLength: smooth }}
                            opacity="0.85"
                        />
                    </svg>

                    {steps.map((s, i) => (
                        <StepBlock key={s.title} {...s} active={i < reached} />
                    ))}

                    {/* Avatar riding the journey */}
                    <motion.div
                        style={{ left, top }}
                        data-testid="how-i-think-avatar"
                        className="absolute z-20 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    >
                        <span className="absolute inset-0 rounded-full bg-[var(--accent)]/30 blur-md animate-pulse" />
                        <span
                            className="absolute -inset-1.5 rounded-full border border-[var(--accent)]/50"
                            style={{ animation: 'spin 6s linear infinite' }}
                        />
                        <img
                            src="/avatars/hero.webp"
                            alt="Himanshu"
                            onError={(e) => {
                                e.currentTarget.src = '/avatars/about.webp';
                            }}
                            className="relative w-14 h-14 lg:w-16 lg:h-16 rounded-full object-cover border-2 border-[var(--accent)] shadow-[0_0_28px_-4px_var(--accent)] bg-[var(--surface)]"
                        />
                    </motion.div>
                </div>

                {/* Mobile: vertical dashed line with scroll-fill + numbered steps */}
                <div className="md:hidden relative">
                    <div className="absolute left-[17px] top-1 bottom-1 w-px border-l border-dashed border-[var(--line-2)]" />
                    <motion.div
                        style={{ scaleY: smooth }}
                        className="absolute left-[16px] top-1 bottom-1 w-[2px] bg-[var(--accent)] origin-top"
                    />
                    <div className="flex flex-col gap-9">
                        {steps.map((s, i) => {
                            const active = i < reached;
                            return (
                                <motion.div
                                    key={s.title}
                                    className="relative pl-12"
                                    animate={{ opacity: active ? 1 : 0.35, y: active ? 0 : 10 }}
                                    transition={{ duration: 0.5, ease: 'easeOut' }}
                                >
                                    <span
                                        className={`absolute left-0 top-0 w-9 h-9 rounded-full grid place-items-center font-mono-brand text-[12px] tabular-nums transition-colors duration-500 ${
                                            active
                                                ? 'bg-[var(--accent)] border border-[var(--accent)] text-black'
                                                : 'bg-[var(--surface)] border border-[var(--line-2)] text-[var(--accent)]'
                                        }`}
                                    >
                                        {s.n}
                                    </span>
                                    <div className="font-mono-brand text-[10px] uppercase tracking-[0.28em] text-[var(--accent)]">
                                        {s.stage}
                                    </div>
                                    <h3 className="mt-1.5 font-display leading-[1.02] text-3xl text-[var(--ink)]">
                                        {s.title}
                                        <span className="text-[var(--accent)]">.</span>
                                    </h3>
                                    <p className="mt-2 font-sans-brand text-[13.5px] leading-[1.6] text-[var(--ink-2)]">
                                        {s.body}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HowIThink;
